import React from 'react';
import PhoneInput from 'react-phone-number-input/input';
import type { CountryCode } from 'libphonenumber-js';
import {
  TextField,
  Box,
  useTheme,
  alpha,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  styled
} from '@mui/material';
import { isValidPhoneNumber, getCountries, getCountryCallingCode, getExampleNumber, parsePhoneNumber, formatIncompletePhoneNumber } from 'libphonenumber-js';
import { getCountryFlag } from './country-flags';
import examples from 'libphonenumber-js/examples.mobile.json';

// Import CSS
import 'react-phone-number-input/style.css';

const countries: CountryCode[] = ['FR', 'BE', 'CH', 'LU', 'MC', 'CA', 'US', 'GB', 'DE', 'ES', 'IT', 'PT', 'NL', 'MA', 'DZ', 'TN'];

// Get example phone numbers for each country
const countryExamples = {
  FR: '6 12 34 56 78',
  BE: '470 12 34 56',
  CH: '78 123 45 67',
  LU: '628 123 456',
  MC: '6 12 34 56 78',
  CA: '506 234 5678',
  US: '201 555 0123',
  GB: '7700 900123',
  DE: '151 2345 6789',
  ES: '612 34 56 78',
  IT: '312 345 6789',
  PT: '912 345 678',
  NL: '6 12345678',
  MA: '650 123456',
  DZ: '551 23 45 67',
  TN: '20 123 456',
};

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    paddingRight: theme.spacing(4),
  },
}));

interface PhoneNumberInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  defaultCountry?: CountryCode;
  size?: 'small' | 'medium';
  name?: string;
  id?: string;
  autoFocus?: boolean;
  margin?: 'none' | 'dense' | 'normal';
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  label = "Téléphone",
  error = false,
  helperText,
  required = false,
  disabled = false,
  fullWidth = true,
  placeholder,
  defaultCountry = 'FR' as CountryCode,
  size = 'medium',
  name,
  id,
  autoFocus = false,
  margin = 'normal',
}) => {
  const theme = useTheme();
  const [country, setCountry] = React.useState<CountryCode>(defaultCountry);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Get dynamic placeholder based on country
  const dynamicPlaceholder = React.useMemo(() => {
    return placeholder || (countryExamples as Record<string, string>)[country] || '123456789';
  }, [country, placeholder]);

  // Extract phone number without country code and format it
  const phoneNumberWithoutCountry = React.useMemo(() => {
    if (!value) return '';
    try {
      const countryCode = getCountryCallingCode(country);
      const prefix = `+${countryCode}`;
      let phoneWithoutCode = '';

      if (value.startsWith(prefix)) {
        phoneWithoutCode = value.slice(prefix.length);
      } else {
        phoneWithoutCode = value.replace(/^\+\d+/, '');
      }

      // Format the number for display
      if (phoneWithoutCode) {
        // Create a full number for formatting
        const fullNumber = `+${countryCode}${phoneWithoutCode.replace(/\s/g, '')}`;
        const formatted = formatIncompletePhoneNumber(fullNumber, country);
        // Remove the country code from formatted number
        if (formatted.startsWith(prefix)) {
          return formatted.slice(prefix.length).trim();
        }
        return phoneWithoutCode;
      }
      return '';
    } catch {
      return value;
    }
  }, [value, country]);

  // Check if phone number is complete and valid
  const checkIfComplete = React.useCallback((phoneNumber: string) => {
    console.log('Checking if phone number is complete:', phoneNumber);
    if (!phoneNumber) {
      console.log('Phone number is empty');
      return false;
    }

    try {
      // Check if it's a valid phone number
      console.log('Validating phone number for country:', country);
      if (isValidPhoneNumber(phoneNumber, country)) {
        console.log('Phone number is valid');
        // Parse the phone number to check if it's complete
        const parsed = parsePhoneNumber(phoneNumber, country);
        console.log('Parsed phone number:', parsed);
        if (parsed && parsed.isValid()) {
          console.log('Phone number is complete and valid');
          // Auto blur the input when number is complete
          setTimeout(() => {
            if (inputRef.current) {
              console.log('Blurring input');
              inputRef.current.blur();
            }
          }, 100);
          return true;
        }
      }
    } catch (error) {
      console.error('Error validating phone number:', error);
      return false;
    }
    console.log('Phone number validation failed');
    return false;
  }, [country]);

  // Handle country change
  const handleCountryChange = (newCountry: CountryCode) => {
    setCountry(newCountry);
    if (phoneNumberWithoutCountry) {
      const countryCode = getCountryCallingCode(newCountry);
      const fullNumber = `+${countryCode}${phoneNumberWithoutCountry}`;
      onChange(fullNumber);
      // Check if the number is complete with the new country code
      checkIfComplete(fullNumber);
    }
  };

  // Handle phone input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      onChange(undefined);
      return;
    }

    // Remove any non-digit characters except spaces
    const digitsOnly = inputValue.replace(/[^\d]/g, '');
    const countryCode = getCountryCallingCode(country);
    const fullNumber = `+${countryCode}${digitsOnly}`;

    // Store the unformatted number
    onChange(fullNumber);

    // Check if number is complete
    checkIfComplete(fullNumber);
  };

  // Validate phone number
  const isValid = value ? isValidPhoneNumber(value) : true;
  const showError = error || (value && !isValid) || false;
  const errorMessage = helperText || (value && !isValid ? 'Numéro de téléphone invalide' : '') || '';

  return (
    <TextField
      label={label}
      error={showError}
      helperText={errorMessage}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      margin={margin}
      name={name}
      id={id}
      autoFocus={autoFocus}
      value={phoneNumberWithoutCountry}
      onChange={handlePhoneChange}
      placeholder={dynamicPlaceholder}
      inputRef={inputRef}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <StyledSelect
              value={country}
              onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
              variant="standard"
              disabled={disabled}
              sx={{
                '& .MuiSelect-standard': {
                  paddingRight: 2,
                },
                '&:before': {
                  display: 'none',
                },
                '&:after': {
                  display: 'none',
                },
              }}
            >
              {countries.map((countryCode) => (
                <MenuItem key={countryCode} value={countryCode}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2em' }}>{getCountryFlag(countryCode)}</span>
                    <span>+{getCountryCallingCode(countryCode)}</span>
                  </Box>
                </MenuItem>
              ))}
            </StyledSelect>
          </InputAdornment>
        ),
      }}
    />
  );
};

// Export a simpler version for forms that handle their own validation
export const SimplePhoneInput: React.FC<{
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  defaultCountry?: CountryCode;
  disabled?: boolean;
  className?: string;
}> = ({
  value,
  onChange,
  placeholder,
  defaultCountry = 'FR' as CountryCode,
  disabled = false,
  className
}) => {
  return (
    <PhoneNumberInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      defaultCountry={defaultCountry}
      disabled={disabled}
    />
  );
};
